import { TFunction } from 'i18next';
import { AzureRowKind } from './acceptance/acceptances';
import { toLocaleDateShort } from 'utils/datetime';
import { AzureReportingAppName } from './connection/AzureConnectionConfig';
import {
  AzureCheck,
  AzureLogDestination,
  AzureLogSource,
  AzureResourceType,
  AzureRow,
  AzureState,
  AzureUsage,
  IAzureResult,
  DefaultRetentionDays,
  DefaultWarnDays,
  isAzureCouldNotCheckState,
  IUsageFact,
} from './AzureModels';

//Text shared by the Azure results view and evidence report.
export const getAzureResourceTypeText = (type: AzureResourceType, t: TFunction<string[]>): string => {
  switch (type) {
    case AzureResourceType.SqlServer:
      return t('integrations:MS_Azure.ResourceTypes.SqlServer');
    case AzureResourceType.PostgreSqlFlexible:
      return t('integrations:MS_Azure.ResourceTypes.PostgreSql');
    case AzureResourceType.MySqlFlexible:
      return t('integrations:MS_Azure.ResourceTypes.MySql');
    case AzureResourceType.KeyVault:
      return t('integrations:MS_Azure.ResourceTypes.KeyVault');
    case AzureResourceType.AppService:
      return t('integrations:MS_Azure.ResourceTypes.AppService');
    case AzureResourceType.KeyVaultKey:
      return t('integrations:MS_Azure.ResourceTypes.Key');
    case AzureResourceType.KeyVaultSecret:
      return t('integrations:MS_Azure.ResourceTypes.Secret');
    default:
      return t('integrations:MS_Azure.ResourceTypes.Subscription');
  }
};

//the widest of the rules that decided the state, as "start - end"
export const getWidestRange = (row: AzureRow): string => {
  const rules = row.database?.rules ?? [];
  if (rules.length === 0) return row.found;
  const widest = rules.reduce((a, b) => (b.addresses > a.addresses ? b : a));

  return `${widest.startIp} - ${widest.endIp}`;
};

const getRequiredDays = (result: IAzureResult): number => result.minimumRetentionDays ?? DefaultRetentionDays;

//
// States
//

//The state of a row in one or two words, for the State column.
export const getAzureStateLabel = (row: AzureRow, t: TFunction<string[]>): string => {
  switch (row.state) {
    case AzureState.OpenToInternet:
      return t('integrations:MS_Azure.States.OpenToInternet.Label');
    case AzureState.WideRange:
      return t('integrations:MS_Azure.States.WideRange.Label');
    case AzureState.OpenToAzure:
      return t('integrations:MS_Azure.States.OpenToAzure.Label');
    case AzureState.AccessPolicies:
      return t('integrations:MS_Azure.States.AccessPolicies.Label');
    case AzureState.NoPurgeProtection:
      return t('integrations:MS_Azure.States.NoPurgeProtection.Label');
    case AzureState.AccessPoliciesAndNoPurgeProtection:
      return t('integrations:MS_Azure.States.AccessPoliciesAndNoPurgeProtection.Label');
    case AzureState.LogOff:
      return t('integrations:MS_Azure.States.LogOff.Label');
    case AzureState.RetentionTooShort:
      return t('integrations:MS_Azure.States.RetentionTooShort.Label');
    case AzureState.RetentionUnknown:
      return t('integrations:MS_Azure.States.RetentionUnknown.Label');
    case AzureState.NoEndDate:
      return t('integrations:MS_Azure.States.NoEndDate.Label');
    case AzureState.ExpiresSoon:
      return t('integrations:MS_Azure.States.ExpiresSoon.Label');
    case AzureState.Expired:
      return t('integrations:MS_Azure.States.Expired.Label');
    case AzureState.KeysNotReadable:
      return t('integrations:MS_Azure.States.KeysNotReadable.Label');
    case AzureState.DestinationNotReadable:
    case AzureState.ActivityLogNotVisible:
    case AzureState.SubscriptionNotReadable:
      return t('integrations:MS_Azure.States.SubscriptionNotReadable.Label');
    case AzureState.PrivateOnly:
      return t('integrations:MS_Azure.States.PrivateOnly.Label');
    case AzureState.Ok:
      return t('integrations:MS_Azure.States.Ok.Label');
    default:
      return t('integrations:MS_Azure.States.Other.Label');
  }
};

//The state of a row in full: what is the case and how to fix it. The tooltip of the State column, and the line in the
//evidence report.
export const getAzureStateDetail = (row: AzureRow, result: IAzureResult, t: TFunction<string[]>): string => {
  switch (row.state) {
    case AzureState.OpenToInternet:
      return t('integrations:MS_Azure.States.OpenToInternet.Detail', { range: getWidestRange(row) });
    case AzureState.WideRange:
      return t('integrations:MS_Azure.States.WideRange.Detail', { range: getWidestRange(row) });
    case AzureState.OpenToAzure:
      return result.allowAzureServicesIsFinding
        ? t('integrations:MS_Azure.States.OpenToAzure.DetailFinding')
        : t('integrations:MS_Azure.States.OpenToAzure.DetailWarning');
    case AzureState.AccessPolicies:
      return t('integrations:MS_Azure.States.AccessPolicies.Detail');
    case AzureState.NoPurgeProtection:
      return t('integrations:MS_Azure.States.NoPurgeProtection.Detail');
    case AzureState.AccessPoliciesAndNoPurgeProtection:
      return t('integrations:MS_Azure.States.AccessPoliciesAndNoPurgeProtection.Detail');
    case AzureState.LogOff:
      switch (row.log?.source) {
        case AzureLogSource.AppServiceHttpLogs:
          return t('integrations:MS_Azure.States.LogOff.DetailAppService');
        case AzureLogSource.ActivityLog:
          return t('integrations:MS_Azure.States.LogOff.DetailActivityLog');
        default:
          return t('integrations:MS_Azure.States.LogOff.DetailSqlAuditing');
      }
    case AzureState.RetentionTooShort:
      return t('integrations:MS_Azure.States.RetentionTooShort.Detail', {
        found: row.log?.retentionDays ?? row.found,
        required: getRequiredDays(result),
      });
    case AzureState.RetentionUnknown:
      return row.log?.destination === AzureLogDestination.EventHub
        ? t('integrations:MS_Azure.States.RetentionUnknown.DetailEventHub')
        : t('integrations:MS_Azure.States.RetentionUnknown.DetailStorage', { required: getRequiredDays(result) });
    case AzureState.DestinationNotReadable:
      return t('integrations:MS_Azure.States.DestinationNotReadable.Detail', { appName: AzureReportingAppName });
    case AzureState.ActivityLogNotVisible:
      return t('integrations:MS_Azure.States.ActivityLogNotVisible.Detail', { appName: AzureReportingAppName });
    case AzureState.NoEndDate:
      return t('integrations:MS_Azure.States.NoEndDate.Detail');
    case AzureState.ExpiresSoon:
      return t('integrations:MS_Azure.States.ExpiresSoon.Detail', {
        date: toLocaleDateShort(row.secret?.expiresUtc),
        days: row.secret?.daysLeft ?? '',
      });
    case AzureState.Expired:
      return t('integrations:MS_Azure.States.Expired.Detail', { date: toLocaleDateShort(row.secret?.expiresUtc) });
    case AzureState.KeysNotReadable:
      return t('integrations:MS_Azure.States.KeysNotReadable.Detail');
    case AzureState.SubscriptionNotReadable:
      return t('integrations:MS_Azure.States.SubscriptionNotReadable.Detail', { appName: AzureReportingAppName });
    case AzureState.PrivateOnly:
      return t('integrations:MS_Azure.States.PrivateOnly.Detail');
    case AzureState.Ok:
      switch (result.check) {
        case AzureCheck.Databases:
          return t('integrations:MS_Azure.States.Ok.DetailDatabases');
        case AzureCheck.KeyVaults:
          return t('integrations:MS_Azure.States.Ok.DetailKeyVaults');
        case AzureCheck.Logs:
          return t('integrations:MS_Azure.States.Ok.DetailLogs');
        default:
          return t('integrations:MS_Azure.States.Ok.DetailSecrets', { days: result.warnDays ?? row.expected });
      }
    default:
      return t('integrations:MS_Azure.States.Other.Detail', { found: row.found, expected: row.expected });
  }
};

//
// Outcome, meta, usage, footer
//
export const getAzureOutcomeText = (result: IAzureResult, t: TFunction<string[]>): string => {
  const findings = result.rows.filter((row) => row.kind === AzureRowKind.Finding);
  const onlyCouldNotCheck = findings.length > 0 && findings.every((row) => isAzureCouldNotCheckState(row.state));

  switch (result.check) {
    case AzureCheck.Databases:
      if (result.pass) return t('integrations:MS_Azure.Outcome.Databases.Pass');
      if (onlyCouldNotCheck) return t('integrations:MS_Azure.Outcome.Databases.CouldNotCheck');

      return t('integrations:MS_Azure.Outcome.Databases.Fail');
    case AzureCheck.KeyVaults:
      if (result.pass) return t('integrations:MS_Azure.Outcome.KeyVaults.Pass');
      if (onlyCouldNotCheck) return t('integrations:MS_Azure.Outcome.KeyVaults.CouldNotCheck');

      return t('integrations:MS_Azure.Outcome.KeyVaults.Fail');
    case AzureCheck.Logs:
      if (result.pass) return t('integrations:MS_Azure.Outcome.Logs.Pass');
      if (onlyCouldNotCheck) return t('integrations:MS_Azure.Outcome.Logs.CouldNotCheck');

      return t('integrations:MS_Azure.Outcome.Logs.Fail');
    default:
      if (result.pass) return t('integrations:MS_Azure.Outcome.Secrets.Pass');
      if (onlyCouldNotCheck) return t('integrations:MS_Azure.Outcome.Secrets.CouldNotCheck');

      return t('integrations:MS_Azure.Outcome.Secrets.Fail');
  }
};

//What was checked: the subscriptions, and the option in force during the run.
export const getAzureMetaLines = (result: IAzureResult, t: TFunction<string[]>): string[] => {
  const counts = result.counts;
  const scope = [
    t('integrations:MS_Azure.Meta.Scope', { read: counts.subscriptionsRead, inScope: counts.subscriptionsInScope }),
  ];
  if (counts.subscriptionsExcluded > 0) {
    scope.push(t('integrations:MS_Azure.Meta.Excluded', { number: counts.subscriptionsExcluded }));
  }
  if (counts.subscriptionsNotReadable > 0) {
    scope.push(t('integrations:MS_Azure.Meta.NotReadable', { number: counts.subscriptionsNotReadable }));
  }
  if (counts.resourceGroupsExcluded > 0) {
    scope.push(t('integrations:MS_Azure.Meta.ResourceGroupsExcluded', { number: counts.resourceGroupsExcluded }));
  }

  const lines = [scope.join(' ')];
  switch (result.check) {
    case AzureCheck.Databases:
      lines.push(
        result.allowAzureServicesIsFinding
          ? t('integrations:MS_Azure.Meta.Databases.AllowAzureIsFinding')
          : t('integrations:MS_Azure.Meta.Databases.AllowAzureIsWarning'),
      );
      break;
    case AzureCheck.KeyVaults:
      lines.push(t('integrations:MS_Azure.Meta.KeyVaults'));
      break;
    case AzureCheck.Logs:
      lines.push(t('integrations:MS_Azure.Meta.Logs.Required'));
      lines.push(t('integrations:MS_Azure.Meta.Logs.Retention', { days: getRequiredDays(result) }));
      break;
    case AzureCheck.Secrets:
      lines.push(t('integrations:MS_Azure.Meta.Secrets', { days: result.warnDays ?? DefaultWarnDays }));
      break;
  }

  return lines;
};

export const getAzureUsageMessage = (result: IAzureResult, t: TFunction<string[]>): string => {
  switch (result.usage) {
    case AzureUsage.NoReadAccess:
      return t('integrations:MS_Azure.Usage.NoReadAccess.Message');
    case AzureUsage.NoSubscriptionInScope:
      return t('integrations:MS_Azure.Usage.NoSubscriptionInScope.Message');
    case AzureUsage.NothingChecked:
      return t('integrations:MS_Azure.Usage.NothingChecked.Message');
    case AzureUsage.ScopeNotValid:
      return t('integrations:MS_Azure.Usage.ScopeNotValid.Message');
    default:
      switch (result.check) {
        case AzureCheck.Databases:
          return t('integrations:MS_Azure.Usage.NotInUse.Databases.Message');
        case AzureCheck.KeyVaults:
          return t('integrations:MS_Azure.Usage.NotInUse.KeyVaults.Message');
        case AzureCheck.Secrets:
          return t('integrations:MS_Azure.Usage.NotInUse.Secrets.Message');
        default:
          return t('integrations:MS_Azure.Usage.NotInUse.Other.Message');
      }
  }
};

export const getAzureUsageDescription = (result: IAzureResult, t: TFunction<string[]>): string => {
  switch (result.usage) {
    case AzureUsage.NoReadAccess:
      return t('integrations:MS_Azure.Usage.NoReadAccess.Description', { appName: AzureReportingAppName });
    case AzureUsage.NoSubscriptionInScope:
      return t('integrations:MS_Azure.Usage.NoSubscriptionInScope.Description');
    case AzureUsage.NothingChecked:
      return t('integrations:MS_Azure.Usage.NothingChecked.Description');
    case AzureUsage.ScopeNotValid:
      return t('integrations:MS_Azure.Usage.ScopeNotValid.Description');
    default:
      switch (result.check) {
        case AzureCheck.Databases:
          return t('integrations:MS_Azure.Usage.NotInUse.Databases.Description');
        case AzureCheck.KeyVaults:
          return t('integrations:MS_Azure.Usage.NotInUse.KeyVaults.Description');
        case AzureCheck.Secrets:
          return t('integrations:MS_Azure.Usage.NotInUse.Secrets.Description');
        default:
          return t('integrations:MS_Azure.Usage.NotInUse.Other.Description');
      }
  }
};

export const getAzureUsageFacts = (result: IAzureResult, t: TFunction<string[]>): IUsageFact[] => {
  return [
    { label: t('integrations:MS_Azure.Usage.Facts.InScope'), value: result.counts.subscriptionsInScope },
    { label: t('integrations:MS_Azure.Usage.Facts.Read'), value: result.counts.subscriptionsRead },
    { label: t('integrations:MS_Azure.Usage.Facts.Excluded'), value: result.counts.subscriptionsExcluded },
    { label: t('integrations:MS_Azure.Usage.Facts.NotReadable'), value: result.counts.subscriptionsNotReadable },
  ];
};
